import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND, ASSETS } from "@/lib/brand";
import { ArrowRight } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Welcome() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invite link — please use the link from your welcome email."); return; }
    api.get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/accept-invite", { token, password });
      if (refresh) await refresh();
      toast.success("Welcome to MTM — your account is active");
      window.location.href = "/app";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg)" }} data-testid="welcome-page">
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "var(--surface-2)" }}>
        <img src={ASSETS.authorityCamel} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-8 left-8 z-10">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-3xl text-white">{BRAND.name}</span>
            <span className="wordmark-tag" style={{ color: "#FFFFFF" }}>{BRAND.tag}</span>
          </Link>
        </div>
        <div className="absolute bottom-12 left-12 right-12 z-10 text-white">
          <p className="overline" style={{ color: "#D4CFC7" }}>You're in</p>
          <h2 className="font-display text-4xl mt-4 leading-tight" style={{ color: "#FFFFFF" }}>
            Welcome to your<br />
            <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "#FFFFFF", letterSpacing: 0 }}>transformation.</span>
          </h2>
          <div className="bronze-rule mt-6" />
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 md:px-16 py-12 max-w-xl w-full mx-auto">
        <div className="lg:hidden flex justify-between items-center mb-12">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-2xl">{BRAND.name}</span>
            <span className="wordmark-tag">{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>

        {error ? (
          <div data-testid="welcome-error">
            <p className="overline">Hmm</p>
            <h1 className="font-display text-4xl mt-6">{error}</h1>
            <Link to="/apply" className="btn-primary mt-10 inline-flex">Apply for coaching <ArrowRight size={12} /></Link>
          </div>
        ) : !invite ? (
          <p className="text-secondary-luxe">Checking your invitation…</p>
        ) : (
          <>
            <p className="overline">Activate your account</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
              Welcome,<br />
              <span className="font-script normal-case" style={{ fontSize: "1.1em", color: "var(--accent)", letterSpacing: 0 }}>
                {invite.name.split(" ")[0].toLowerCase()}.
              </span>
            </h1>
            <div className="bronze-rule mt-6" />
            <p className="font-soft text-lg mt-8" style={{ color: "var(--text-secondary)" }}>
              Your MTM enrollment is confirmed. Choose a password to step into your member portal.
            </p>
            <form onSubmit={submit} className="mt-10 space-y-7" data-testid="welcome-form">
              <div>
                <label className="label-luxe">Email</label>
                <input className="input-luxe" value={invite.email} disabled data-testid="welcome-email" />
              </div>
              <div>
                <label className="label-luxe">Choose a password</label>
                <input type="password" required minLength={6} className="input-luxe" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="welcome-password" />
              </div>
              <div>
                <label className="label-luxe">Confirm password</label>
                <input type="password" required minLength={6} className="input-luxe" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="welcome-confirm" />
              </div>
              <button className="btn-primary w-full justify-center" disabled={busy} data-testid="welcome-submit">
                {busy ? "Activating…" : "Activate & enter"} <ArrowRight size={12} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
